// Inline signature preview for composers — shows the signature exactly as it
// will be appended, rendered in a sandboxed iframe so its inline styles don't
// fight the app CSS (and vice versa). Auto-grows to fit, capped at 140 px.

import { useEffect, useMemo, useRef, useState } from 'react';
import { signaturesForAccount, useSignatures } from '@/lib/signatures';

export function SignatureInlinePreview({
  accountId,
  signatureId,
}: {
  accountId: number | null;
  /** Explicit pick; undefined = the account's default signature. */
  signatureId?: number | null;
}) {
  const sigs = useSignatures();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(48);

  const html = useMemo(() => {
    const list = signaturesForAccount(sigs.data?.signatures, accountId);
    if (list.length === 0) return null;
    const picked = signatureId != null ? list.find((s) => s.id === signatureId) : undefined;
    return (picked ?? list[0])!.html;
  }, [sigs.data, accountId, signatureId]);

  const srcdoc = useMemo(() => {
    if (!html) return '';
    return `<!doctype html><meta charset="utf-8"><base target="_blank">
<style>
  html,body{margin:0;padding:6px 8px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif;font-size:13px;color:#1f2937;background:white;}
  img{max-width:100%;height:auto;}
</style>
<body>${html}
<script>
(function(){
  function post(){
    parent.postMessage({type:'wharf-sig-size',height:document.documentElement.scrollHeight},'*');
  }
  window.addEventListener('load',post);
  document.querySelectorAll('img').forEach(function(i){ if(!i.complete){ i.addEventListener('load',post); i.addEventListener('error',post); } });
  setTimeout(post,100);
})();
</script>
</body>`;
  }, [html]);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const d = e.data as { type?: string; height?: number } | null;
      if (d?.type === 'wharf-sig-size' && typeof d.height === 'number') {
        setHeight(Math.min(140, Math.max(32, d.height + 4)));
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (!html) return null;

  return (
    <div className="mt-1 border-t border-dashed pt-1" aria-label="Signature preview">
      <iframe
        ref={iframeRef}
        title="Signature"
        srcDoc={srcdoc}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        className="pointer-events-none block w-full rounded-md"
        style={{ height, border: 0, background: 'white' }}
      />
    </div>
  );
}
